import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { completeSignIn, GoogleRedirectError } from './googleAuth';

/**
 * Where Google sends the user back after a sign-in.
 *
 * Unlike the other providers' callback routes this is never a popup talking
 * to an opener - Google redirects on every platform - so it is the app
 * itself, mid-navigation, and the only page loaded at this point. It redeems
 * the code through the backend, which writes the token store, and pushes the
 * user back where they were. /google-redirect renders outside the service
 * tree (see App.tsx), so GapiService mounts fresh on arrival and reads the
 * store on mount - that is the handover.
 *
 * It deliberately renders almost nothing. Anything heavier would be a second
 * thing that can fail between the user having consented and the app knowing
 * about it.
 */
export default function GoogleRedirect() {
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const [returnTo, setReturnTo] = useState('/app');

  // The work is a one-shot side effect. A re-run would find the code already
  // redeemed - they are single-use - and report a spurious failure.
  const handled = useRef(false);

  useEffect(
    function completeGoogleSignIn() {
      if (handled.current) {
        return;
      }

      handled.current = true;

      completeSignIn(window.location.search).then(
        function handOff(result) {
          // replace, not push: the URL still carries a spent authorisation
          // code, and there is no reason to let Back land on a callback that
          // can no longer be completed.
          navigate(result.returnTo, { replace: true });
        },
        function reportFailure(err: GoogleRedirectError) {
          setReturnTo(err.returnTo || '/app');
          setError(err.message || 'Google sign-in failed.');
        },
      );
    },
    [navigate],
  );

  if (!error) {
    return <p>Completing Google sign-in…</p>;
  }

  return (
    <article className="mainViewport">
      <section>
        <h5>Google sign-in did not complete</h5>
        <p>{error}</p>
        <button className="button" onClick={() => navigate(returnTo, { replace: true })}>
          Back to Fadila
        </button>
      </section>
    </article>
  );
}
